<script setup lang="ts">
import { TabGroup, TabList, Tab, TabPanels, TabPanel } from '@headlessui/vue';

const route = useRoute();

useHead({
  title: 'ZapScale Black Friday Offer',
  meta: [
    {
      name: 'description',
      content: 'Onboard ZapScale between 20th-24th November and get 40% off',
    },
    {
      property: 'og:description',
      content: 'Onboard ZapScale between 20th-24th November and get 40% off',
    },
    {
      property: 'og:title',
      content: 'ZapScale Black Friday Offer', // same as title
    },
    {
      property: 'og:url',
      content: 'https://www.zapscale.com' + route.path,
    },
    {
      property: 'og:image',
      content: 'https://www.zapscale.com/images/logo.png',
    },
    {
      name: 'twitter:card',
      content: 'summary',
    },
  ],
  link: [
    {
      rel: 'canonical',
      href: 'https://www.zapscale.com' + route.path,
    },
  ],
});

useJsonld(() => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'ZapScale',
  description:
    'A single-source customer success management SaaS platform with 50 pre-built playbooks, 40 out-of-the-box KPIs, and unlimited user logins.',
  url: 'https://www.zapscale.com/',
  logo: 'https://www.zapscale.com/images/Header_logo.svg',
  sameAs: [
    'https://www.facebook.com/zapscale',
    'https://www.linkedin.com/company/zapscale/',
    'https://twitter.com/ZapScale',
  ],
}));

// const banner = ref<HTMLImageElement>();
// const tabGroup = ref<HTMLDivElement>();

const showHubspotPopup = ref(false);
const showVideoPopup = ref(false);
const selectedPopupTabIndex = ref(0);
const selectedTabGroupIndex = ref<number>(0);

const videoLink = ref('');
const meetingLink = ref('https://meetings.hubspot.com/ksengupta/black-friday?embed=true');
const hubspotFormId = ref('');
const hubspotFormSubmit = ref(false);

const integrations = [
  {
    name: 'gmail',
    width: '153',
    height: '46',
  },
  // {
  //   name: 'outlook',
  //   width: '141',
  //   height: '55',
  // },
  {
    name: 'slack',
    width: '125',
    height: '32',
  },
  {
    name: 'intercom',
    width: '156',
    height: '47',
  },
  // {
  //   name: 'teams',
  //   width: '169',
  //   height: '48',
  // },
  // {
  //   name: 'calendar',
  //   width: '133',
  //   height: '53',
  // },
  {
    name: 'zendesk',
    width: '116',
    height: '30',
  },
  {
    name: 'segment',
    width: '149',
    height: '37',
  },
  {
    name: 'pendo',
    width: '128',
    height: '35',
  },
  {
    name: 'hubspot',
    width: '128',
    height: '37',
  },
  {
    name: 'jira',
    width: '100',
    height: '68',
  },
  {
    name: 'mixpanel',
    width: '128',
    height: '43',
  },
];

const faqs = ref([
  {
    isOpen: false,
    id: 'faq-1',
    tags: [],
    question: 'Is the Black Friday deal also available for Startup plan?',
    answer: `<p class="mb-6">No, the Black Friday deal is currently only for the growth plan. If you’re an early stage, talk to us for special discounts.</p>`,
  },
  {
    isOpen: false,
    id: 'faq-2',
    tags: [],
    question: 'Is there an additional setup or implementation fees?',
    answer: `<p class="mb-6">There is no extra onboarding or implementation fees. The cost of $1200/month includes onboarding, training and support.</p>`,
  },
  {
    isOpen: false,
    id: 'faq-3',
    tags: [],
    question: 'Are there monthly or quarterly payment options?',
    answer: `<p class="mb-6">Unfortunately, we only offer annual contract and payments.</p>`,
  },
  // {
  //   isOpen: false,
  //   id: 'faq-4',
  //   tags: [],
  //   question: 'What is the timeline for implementation?',
  //   answer: `<p class="mb-6">You can onboard on ZapScale in 1 day. However, there will be 2 days required for prepping your data pre-onboarding and 2 days for training. Typically, the entire project is completed in one week.</p>`,
  // },
  {
    isOpen: false,
    id: 'faq-4',
    tags: [],
    question: 'What payment methods do you accept?',
    answer: `<p class="mb-6">Payment for ZapScale can be made via Stripe, Credit Card or Bank Transfer.</p>`,
  },
]);

const top = ref(0);
const height = ref(0);

const addPosition = computed(() => {
  const value = { top: '' };

  if (top.value) {
    value.top = `${top.value - 27}px`;
  }

  return value;
});

const addHeight = computed(() => {
  const value = { height: '' };

  if (height.value) {
    value.height = `${top.value + height.value + 30}px`;
  }

  return value;
});

const cardsArr = [
  {
    title: 'Know your customer better',
    features: [
      { title: 'Customer 360 view', id: '#customer-360' },
      { title: 'Health Analytics', id: '#health-analytics' },
      { title: 'Account Segmentation', id: '#account-segmentation' },
      { title: 'Outcome Tracking', id: '#outcome-tracking' },
    ],
    image: 'Group',
    tabGroupId: 'tab-group-1',
  },
  {
    title: 'Run your CS ops better',
    features: [
      { title: 'Email Campaigns', id: '#email-campaigns' },
      { title: 'Task Management', id: '#task-management' },
      { title: 'Success Playbook', id: '#success-playbook' },
      { title: 'AI Assistant', id: '#ai-assistant' },
    ],
    image: 'Group-1',
    tabGroupId: 'tab-group-2',
  },
  {
    title: 'Get better business insights ',
    features: [
      { title: 'Business Overview', id: '#business-overview' },
      { title: 'Prediction Alerts', id: '#prediction-alerts' },
      { title: 'Trend Analytics', id: '#trend-analytics' },
    ],
    image: 'Group-2',
    tabGroupId: 'tab-group-3',
  },
];

function toggleOpen(id: string) {
  faqs.value.map((x) => {
    if (x.id === id) {
      x.isOpen = !x.isOpen;
    } else {
      x.isOpen = false;
    }
  });
}

function toggleDisclosure(btnId: string) {
  const disclosureButton = document.getElementById(btnId) as HTMLElement;
  toggleOpen(btnId);
  const panel = disclosureButton.nextElementSibling as HTMLElement;
  const disclosurePanel = document.getElementsByClassName('faq-panel') as HTMLCollectionOf<HTMLElement>;
  const faqAccordionActive = document.getElementsByClassName('faq-accordion active');
  if (panel.style.maxHeight) {
    panel.style.maxHeight = '';
    disclosureButton.classList.remove('active');
  } else {
    for (let ii = 0; ii < faqAccordionActive.length; ii++) {
      faqAccordionActive[ii].classList.remove('active');
    }
    for (let iii = 0; iii < disclosurePanel.length; iii++) {
      disclosureButton.classList.remove('active');
      disclosurePanel[iii].style.maxHeight = '';
    }
    panel.style.maxHeight = panel.scrollHeight + 'px';
    disclosureButton.classList.add('active');
  }
}

function openPopup(link: string, type: string) {
  meetingLink.value = link;

  if (type === 'product') {
    hubspotFormId.value = 'b1da6114-cda4-409b-9b80-0cf01ebebaaf';
  }

  if (type === 'demo') {
    hubspotFormId.value = '7972ff20-18e3-42aa-98d7-ce0d1bf1025a';
  }

  if (type === 'onboarding') {
    hubspotFormId.value = 'b8b78873-fcc4-4005-97a3-76bb34927dd6';
  }

  selectedPopupTabIndex.value = 0;

  showHubspotPopup.value = true;

  loadHubspotBookingWidget();
}

function closePopup() {
  showHubspotPopup.value = false;

  selectedPopupTabIndex.value = 0;
}

function onHubspotFormSubmit(e: MessageEvent) {
  // console.log('event', e);

  if (e.data.eventName === 'onFormSubmitted' && e.data.type === 'hsFormCallback') {
    const hubspotAnchor = document.getElementById('black-friday-form') as HTMLElement;
    hubspotFormSubmit.value = true;
    hubspotAnchor.classList.add('hidden');
  }
}

function changePopupTabIndex() {
  selectedPopupTabIndex.value = 1;

  if (process.client) {
    (window as any).hbspt.forms.create({
      region: 'na1',
      portalId: '21917371',
      // formId: '7972ff20-18e3-42aa-98d7-ce0d1bf1025a', //demo
      // formId: 'b8b78873-fcc4-4005-97a3-76bb34927dd6', //Onboarding
      // formId: 'b1da6114-cda4-409b-9b80-0cf01ebebaaf', //product
      formId: hubspotFormId.value,
      target: '#black-friday-form',
    });

    window.addEventListener('message', onHubspotFormSubmit);
  }
}

function loadHubspotBookingWidget() {
  const interval = setInterval(function () {
    if (document.getElementById('dialog')) {
      const hubspotDemoScript = document.createElement('script');

      hubspotDemoScript.setAttribute('src', 'https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js');
      hubspotDemoScript.setAttribute('type', 'text/javascript');

      document.body.appendChild(hubspotDemoScript);

      window.addEventListener('message', onMessageEvent);

      clearInterval(interval);
    }
  }, 300);
}

function isHubspotBookingEvent(e: MessageEvent) {
  return (
    e.origin === 'https://meetings.hubspot.com' &&
    e.data.meetingsPayload &&
    (e.data.meetingsPayload.userSlug === 'ksengupta/black-friday' ||
      e.data.meetingsPayload.userSlug === 'ksengupta/demo-account' ||
      e.data.meetingsPayload.userSlug === 'ksengupta/sign-up-for-onboarding')
  );
}

function onMessageEvent(e: MessageEvent) {
  // console.log('event', e);

  if (isHubspotBookingEvent(e) && e.data.meetingBookSucceeded) {
    changePopupTabIndex();
  }
}

function changeTabIndex(index: number) {
  selectedTabGroupIndex.value = index;
}

function openVideoPopup(link: string) {
  videoLink.value = link;

  showVideoPopup.value = true;
}

function closeVideoPopup() {
  showVideoPopup.value = false;
}

if (process.client) {
  const bannerResizeObserver = new ResizeObserver((entries) => {
    if (entries.length) {
      top.value = entries[0].contentRect.height;
    }
  });

  const tabGroupResizeObserver = new ResizeObserver((entries) => {
    if (entries.length) {
      height.value = entries[0].contentRect.height;
    }
  });

  onMounted(() => {
    const banner = document.querySelector('#banner');
    const tabGroup = document.querySelector('#tab-group');

    if (banner) {
      bannerResizeObserver.observe(banner);
    }

    if (tabGroup) {
      tabGroupResizeObserver.observe(tabGroup);
    }
  });

  onUnmounted(() => {
    bannerResizeObserver.disconnect();
    tabGroupResizeObserver.disconnect();
  });
}
</script>

<template>
  <div class="">
    <NuxtImg
      id="banner"
      src="/images/black-friday/black-friday-offer-banner.webp"
      class="-mt-4 block w-full"
      alt="black-friday-banner"
      width="1440"
      height="660"
      :placeholder="[1440, 660]"
      preload
    />

    <div class="container mx-auto px-5 pb-6 text-center md:pb-12">
      <div>
        <p
          class="mx-auto mb-4 mt-6 font-merriweather text-base font-semibold text-[#3a3a3c] md:mb-6 md:mt-14 md:w-[65%] md:text-xl"
        >
          ZapScale is the world’s easiest customer success platform, designed to dive deeper into customer health
          dynamics and business performance like never before.
        </p>
        <p class="font-merriweather text-base font-semibold text-[#3a3a3c] md:text-xl">ZapScale Features:</p>
        <div class="mt-6 flex flex-col justify-center gap-x-10 gap-y-6 px-11 md:mt-12 md:flex-row">
          <div
            v-for="(card, index) in cardsArr"
            :key="index"
            class="flex flex-col justify-center gap-y-5 rounded-3xl bg-[#F5F5F5] px-5 py-10"
          >
            <h2 class="mx-auto max-w-[75%] text-center text-lg font-extrabold text-[#3a3a3c]">
              {{ card.title }}
            </h2>
            <NuxtImg
              :src="`/images/${card.image}.svg`"
              class="h-[8.125rem]"
              :alt="card.title"
              width="240"
              height="130"
            />
            <ul class="space-y-1">
              <li
                v-for="(feature, index) in card.features"
                :key="index"
                :id="card.tabGroupId + '-' + index"
                class="list-element cursor-pointer text-center text-sm font-medium text-[#3a3a3c] underline"
              >
                <NuxtLink :to="`/features/${feature.id}`" class="underline underline-offset-2">
                  {{ feature.title }}</NuxtLink
                >
              </li>
            </ul>
          </div>
        </div>
        <div class="mt-10 flex flex-col items-center justify-center gap-x-6 gap-y-3 md:flex-row">
          <NuxtLink
            to="/demo/#book-a-demo"
            target="_blank"
            class="flex h-[50px] w-[268px] flex-row items-center justify-center gap-x-3 rounded-full bg-[#44B96D] text-base font-bold text-white"
          >
            <NuxtImg src="/images/demo.svg" alt="demo-icon" width="14" height="14" />
            Book A Demo
          </NuxtLink>
        </div>
      </div>

      <div>
        <!-- <p
          class="mx-auto mt-6 text-center font-merriweather text-base font-semibold text-[#3a3a3c] md:mt-14 md:w-[85%] md:text-xl"
        >
          ZapScale’s exciting Black Friday offer is valid for all sign ups before 3rd December 2024.
        </p> -->
        <div class="relative mx-auto mt-10 rounded-3xl md:h-[420px] lg:w-[968px]">
          <NuxtImg
            src="/images/black-friday/black-friday-logo.svg"
            alt="black-friday"
            width="370.04"
            height="240.74"
            class="absolute -top-8 right-16 w-[200px] md:-top-14 md:right-20 md:w-[370.04px]"
          />
          <div
            class="rounded-t-3xl bg-[#44B96D] bg-right bg-no-repeat px-8 py-10 text-left md:h-[266px] md:bg-[url('/images/panel-bg.svg')] md:px-14"
          >
            <p class="text-[26px] font-normal text-white max-md:pt-14 md:w-2/5">
              Get <span class="font-bold">12 months</span> for
              <span class="line-through decoration-2">$24,000</span>
            </p>
            <span class="text-6xl font-extrabold text-[#F5D617]">$14,400</span>
            <span class="block text-lg font-semibold text-white"
              >Per month cost <span class="font-extrabold">$1200</span></span
            >
          </div>
          <div class="rounded-b-3xl border-b border-l border-r px-8 py-5 md:h-[154px]">
            <p class="text-left text-base font-bold text-[#3A3A3C]">Included in the plan</p>
            <div class="grid grid-cols-1 text-base font-medium md:grid-cols-5">
              <span class="col-span-2 flex items-center gap-x-2 whitespace-nowrap">
                <NuxtImg src="/images/green-tick.svg" alt="green-tick" width="10.5" height="7.64" />
                unlimited customers and users
              </span>
              <span class="col-span-2 flex items-center gap-x-2 whitespace-nowrap">
                <NuxtImg src="/images/green-tick.svg" alt="green-tick" width="10.5" height="7.64" />
                unlimited product features / event tracked
              </span>
              <span class="col-span-1 flex items-center gap-x-2 whitespace-nowrap">
                <NuxtImg src="/images/green-tick.svg" alt="green-tick" width="10.5" height="7.64" />
                unlimited data retention
              </span>
              <span class="col-span-2 flex items-center gap-x-2 whitespace-nowrap">
                <NuxtImg src="/images/green-tick.svg" alt="green-tick" width="10.5" height="7.64" />
                unlimited users in your organization
              </span>
              <span class="col-span-2 flex items-center gap-x-2 whitespace-nowrap">
                <NuxtImg src="/images/green-tick.svg" alt="green-tick" width="10.5" height="7.64" />
                unlimited app integration
              </span>
              <span class="col-span-1 flex items-center gap-x-2 whitespace-nowrap">
                <NuxtImg src="/images/green-tick.svg" alt="green-tick" width="10.5" height="7.64" />
                1 day onboarding
              </span>
            </div>
          </div>
        </div>
        <!-- <div class="mt-10 flex flex-col items-center justify-center gap-x-6 gap-y-3 md:flex-row">
          <NuxtLink
            to="/demo/#book-a-demo"
            target="_blank"
            class="flex h-[50px] w-[268px] flex-row items-center justify-center gap-x-3 rounded-full bg-[#44B96D] text-base font-bold text-white"
          >
            <NuxtImg src="/images/demo.svg" alt="demo-icon" width="14" height="14" />
            Book A Demo
          </NuxtLink>
        </div> -->
      </div>
    </div>

    <div class="h-fit bg-[#EFF7FD]">
      <div class="container mx-auto flex justify-center px-5 py-4 md:py-10">
        <div class="mx-auto flex flex-col items-center gap-x-7 gap-y-6 max-md:w-[90%]">
          <div class="flex flex-col items-center">
            <h2 class="font-merriweather text-3xl font-black text-[#1D1D1D] md:text-[51.21px]">Voted #1</h2>
            <span class="block whitespace-nowrap text-lg font-extrabold text-[#1D1D1D] md:text-xl">EASIEST TO USE</span>
          </div>
          <div
            class="flex flex-col items-center gap-x-2 gap-y-4 md:flex-row md:items-start md:justify-center xl:gap-x-4"
          >
            <div class="grid grid-cols-3 items-start gap-x-2 xl:gap-x-4">
              <NuxtImg
                class="col-span-1"
                src="/images/home/g2/spring-badges/CustomerSuccess_EasiestToUse_EaseOfUse.webp"
                alt="easiest-to-use"
                width="76"
                height="84"
              />
              <NuxtImg
                class="col-span-1"
                src="/images/home/g2/spring-badges/CustomerSuccess_BestEstimatedROI_Small-Business_Roi.webp"
                alt="best-estimate-roi"
                width="76"
                height="84"
              />
              <NuxtImg
                class="col-span-1"
                src="/images/home/g2/spring-badges/CustomerSuccess_BestMeetsRequirements_Small-Business_MeetsRequirements.webp"
                alt="best-meets-requirements"
                width="76"
                height="84"
              />
            </div>
            <div class="grid grid-cols-2 items-start gap-x-2 xl:gap-x-4">
              <NuxtImg
                class="col-span-1"
                src="/images/home/g2/spring-badges/CustomerSuccess_FastestImplementation_Small-Business_GoLiveTime.webp"
                alt="fastest-implementation"
                width="97"
                height="108"
              />
              <NuxtImg
                class="col-span-1"
                src="/images/home/g2/spring-badges/CustomerSuccess_EasiestSetup_Small-Business_EaseOfSetup.webp"
                alt="easiest-setup"
                width="97"
                height="108"
              />
            </div>
            <div class="grid grid-cols-3 items-start gap-x-2 xl:gap-x-4">
              <NuxtImg
                class="col-span-1"
                src="/images/home/g2/spring-badges/CustomerSuccess_BestSupport_Small-Business_QualityOfSupport.webp"
                alt="small-business-quality-of-support"
                width="76"
                height="84"
              />
              <NuxtImg
                class="col-span-1"
                src="/images/home/g2/spring-badges/CustomerSuccess_UsersMostLikelyToRecommend_Small-Business_Nps.webp"
                alt="users-most-likely-to-recommend"
                width="76"
                height="84"
              />
              <NuxtImg
                class="col-span-1"
                src="/images/home/g2/spring-badges/CustomerSuccess_HighPerformer_HighPerformer.webp"
                alt="high-performer"
                width="76"
                height="84"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="container mx-auto px-5 py-4 md:py-10">
      <h2
        class="mx-auto mb-4 max-w-4xl px-4 text-center font-merriweather text-lg font-black text-[#3a3a3c] md:mb-10 md:text-4xl md:font-extrabold"
      >
        Loved by the Customer Success teams at:
      </h2>
      <CustomerLogoSlider />
    </div>

    <div class="container mx-auto px-5">
      <div class="mx-auto mb-10 rounded-2xl bg-[#44B96D] py-10 md:mb-16 lg:h-[326px] lg:w-[968px]">
        <p class="text-center font-merriweather text-[45px] font-normal text-white">
          40% off on ZapScale CSP<span class="block font-extrabold"> Now at $1200/month</span>
        </p>
        <div class="mt-10 flex flex-col items-center justify-center gap-x-6 gap-y-3 md:flex-row">
          <button
            @click="openVideoPopup('https://www.youtube.com/embed/n7xjfcJP2ps?autoplay=1&mute=1')"
            class="flex h-[50px] w-[268px] flex-row items-center justify-center gap-x-3 rounded-full border border-white bg-[#44B96D] text-base font-bold text-white"
          >
            <NuxtImg src="/images/black-friday/play.svg" alt="play" width="29" height="29" loading="lazy" />
            Watch Product Video
          </button>
          <NuxtLink
            to="/demo/#book-a-demo"
            target="_blank"
            class="flex h-[50px] w-[268px] flex-row items-center justify-center gap-x-3 rounded-full bg-white text-base font-bold text-[#44B96D]"
          >
            <NuxtImg src="/images/demo-green.svg" alt="demo-icon" width="14" height="14" loading="lazy" />
            Book A Demo
          </NuxtLink>
        </div>
      </div>
    </div>

    <div class="container mx-auto px-5">
      <LazyNewTestimonials />
    </div>

    <div class="bg-[#EFF7FD]">
      <div class="container mx-auto">
        <div class="px-5 py-10 text-[#19274D] md:px-14">
          <span class="mx-auto mb-4 block w-fit text-base font-normal md:mb-8">INTEGRATION</span>
          <span
            class="mx-auto mb-6 block text-center font-merriweather text-3xl font-bold max-md:leading-9 md:w-[607px] xl:text-[35px]"
            >ZapScale integrates easily with all your favourite apps</span
          >
          <div class="mb-4 hidden items-center justify-center gap-x-4 xl:flex">
            <div
              v-for="integration in integrations.slice(0, 5)"
              class="col-span-1 flex h-[92px] w-[226px] items-center justify-center rounded-2xl border-[1px] border-[#D7E4EE] bg-[#FDFDFD]"
            >
              <NuxtImg
                loading="lazy"
                :placeholder="[integration.width, integration.height]"
                :src="`/images/home/integrations/${integration.name}.svg`"
                :width="integration.width"
                :height="integration.height"
                :alt="integration.name"
              />
            </div>
          </div>
          <div class="mb-12 hidden items-center justify-center gap-x-4 xl:flex">
            <div
              v-for="integration in integrations.slice(5)"
              class="col-span-1 flex h-[92px] w-[226px] items-center justify-center rounded-2xl border-[1px] border-[#D7E4EE] bg-[#FDFDFD]"
            >
              <NuxtImg
                loading="lazy"
                :placeholder="[integration.width, integration.height]"
                :src="`/images/home/integrations/${integration.name}.svg`"
                :width="integration.width"
                :height="integration.height"
                :alt="integration.name"
              />
            </div>
          </div>

          <div class="mb-6 grid grid-cols-3 gap-4 xl:hidden">
            <div
              v-for="integration in integrations"
              class="col-span-1 flex h-[64px] items-center justify-center rounded-2xl border-[1px] border-[#D7E4EE] bg-[#FDFDFD] max-md:p-2"
            >
              <NuxtImg
                loading="lazy"
                :placeholder="[integration.width, integration.height]"
                :src="`/images/home/integrations/${integration.name}.svg`"
                :width="integration.width"
                :height="integration.height"
                :alt="integration.name"
                class="max-md:w-4/5"
              />
            </div>
          </div>

          <NuxtLink
            to="/integrations"
            target="_blank"
            class="mx-auto block h-[41px] w-[179px] rounded-full bg-[#FF395B] px-6 py-2 text-base font-semibold text-white hover:border hover:border-[#FF395B] hover:bg-white hover:text-[#FF395B]"
          >
            <span class="flex items-center justify-between">
              View All
              <Icon name="solar:arrow-right-linear" />
            </span>
          </NuxtLink>
        </div>
      </div>
    </div>

    <div class="bg-white">
      <div class="container mx-auto px-5 py-6 text-center md:py-12">
        <div class="mb-12 md:mb-20">
          <span class="mx-auto mb-3 block w-fit text-base font-normal md:mb-8">SECURITY & COMPLIANCE</span>
          <span
            class="mx-auto mb-7 block w-fit text-center font-merriweather text-3xl font-bold md:mb-10 xl:text-[35px]"
          >
            We make sure your data is safe and secure.
            <br />Data security is our top priority</span
          >
          <div
            class="mx-auto mb-10 flex w-fit flex-col items-center justify-center gap-x-6 gap-y-6 md:mb-16 lg:flex-row lg:gap-y-0"
          >
            <div class="flex items-center justify-center gap-x-6 lg:gap-x-8">
              <div class="flex w-[124px] flex-col items-center md:w-[200px] md:flex-row">
                <NuxtImg
                  loading="lazy"
                  :placeholder="[100, 100]"
                  src="/images/home/soc.png"
                  alt="soc"
                  class="max-lg:w-[80px]"
                  width="100"
                  height="100"
                />
                <div class="max-md:pt-3 max-md:text-center md:pl-4">
                  <span class="block text-sm font-bold text-blue-900 lg:whitespace-nowrap">SOC2 - TYPE II</span>
                  <span class="text-xs font-medium tracking-widest">CERTIFIED</span>
                </div>
              </div>
              <div class="flex w-[124px] flex-col items-center md:w-[200px] md:flex-row">
                <NuxtImg
                  loading="lazy"
                  :placeholder="[95, 100]"
                  src="/images/home/vapt.png"
                  alt="vapt"
                  class="max-lg:w-[80px]"
                  width="95"
                  height="100"
                />
                <div class="max-md:pt-3 max-md:text-center md:pl-4">
                  <span class="block text-sm font-bold text-blue-900">VAPT</span>
                  <span class="text-xs font-medium tracking-widest">COMPLIANT</span>
                </div>
              </div>
            </div>
            <div class="flex items-center justify-center gap-x-6 lg:gap-x-8">
              <div class="flex w-[124px] flex-col items-center md:w-[200px] md:flex-row">
                <NuxtImg
                  loading="lazy"
                  :placeholder="[100, 100]"
                  src="/images/home/gdpr.png"
                  alt="gdpr"
                  class="max-lg:w-[80px]"
                  width="100"
                  height="100"
                />
                <div class="max-md:pt-3 max-md:text-center md:pl-4">
                  <span class="block text-sm font-bold text-blue-900">GDPR</span>
                  <span class="text-xs font-medium tracking-widest">READY</span>
                </div>
              </div>
              <div class="flex w-[124px] flex-col items-center md:w-[200px] md:flex-row">
                <NuxtImg
                  loading="lazy"
                  :placeholder="[100, 100]"
                  src="/images/home/casa.jpg"
                  alt="casa"
                  class="max-lg:w-[80px]"
                  width="100"
                  height="100"
                />
                <div class="max-md:pt-3 max-md:text-center md:pl-4">
                  <span class="block text-sm font-bold text-blue-900">TIER 2</span>
                  <span class="text-xs font-medium tracking-widest md:whitespace-nowrap">CASA VERIFIED</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <h2 class="text-center font-merriweather text-3xl font-bold xl:text-[35px]">Frequently Asked Questions</h2>
        <div id="disclosure-container" :class="['mt-5 flex max-h-[110vh] w-full flex-col gap-y-4 md:mt-20']">
          <div v-for="faq in faqs" :id="'disclosure-' + faq.id" class="flex flex-col md:px-8">
            <button
              :id="faq.id"
              @click="toggleDisclosure(faq.id)"
              :class="[
                'faq-accordion',
                'grid w-full grid-cols-11 items-center rounded-[59px] bg-[#F9F8F5] px-6 py-2 text-left font-medium text-[#3A3A3A] focus:outline-none md:px-8 md:py-3 lg:px-10',
              ]"
            >
              <span :class="['col-span-10 text-sm md:text-xl', faq.isOpen ? 'font-bold' : 'font-semibold']">{{
                faq.question
              }}</span>
              <svg
                v-if="!faq.isOpen"
                class="col-span-1 ml-auto h-5 w-5 md:h-6 md:w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="2"
                stroke="#66B774"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>

              <svg
                v-else
                class="col-span-1 ml-auto h-5 w-5 md:h-6 md:w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="2"
                stroke="#66B774"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
            <div :class="['faq-panel', 'text-sm text-[#3A3A3A]']">
              <article
                class="py-42 mt-3 px-6 text-left text-sm font-normal text-[#3a3a3c] md:px-8 md:text-xl lg:px-10"
                v-html="faq.answer"
              ></article>
            </div>
          </div>
        </div>
        <NuxtLink
          to="/faq"
          target="_blank"
          class="mx-auto mt-8 flex items-center justify-center gap-x-2 text-center text-sm font-semibold text-[#44B96D] md:mt-16 md:w-1/2 md:text-lg"
        >
          Check out the FAQ page for product-related questions
          <NuxtImg
            loading="lazy"
            src="/images/black-friday/arrow-tilted.svg"
            class="w-3 md:w-5"
            alt="arrow-tilted"
            width="22.63"
            height="22.63"
          />
        </NuxtLink>
      </div>
    </div>

    <div class="bg-[#44B96D]">
      <div class="container relative mx-auto px-5 text-center max-md:pb-4 max-md:pt-32 md:py-6 lg:py-12">
        <NuxtImg
          loading="lazy"
          src="/images/black-friday/black-friday-logo.svg"
          alt="black-friday"
          width="235.96"
          height="153.51"
          class="absolute -top-8 w-[235.96px] max-md:right-10 md:right-20"
        />
        <div class="mx-auto max-md:px-2 md:w-4/5">
          <p class="text-left font-merriweather text-3xl font-normal text-white md:pl-8 md:text-[45px] lg:pl-16">
            40% off on ZapScale CSP<span class="mt-5 block font-extrabold"> Now at $1200/month</span>
          </p>
          <div class="mt-6 flex flex-col items-center justify-center gap-x-6 gap-y-3 md:mt-10 lg:flex-row">
            <NuxtLink
              to="/demo/#book-a-demo"
              target="_blank"
              class="flex h-[50px] w-[268px] flex-row items-center justify-center gap-x-3 rounded-full border border-white bg-[#44B96D] text-base font-bold text-white"
            >
              <NuxtImg
                loading="lazy"
                src="/images/black-friday/demo-white.svg"
                alt="demo-icon"
                width="14"
                height="14"
              />
              Book A Demo
            </NuxtLink>
          </div>
        </div>
      </div>
    </div>

    <div id="dialog" class="relative z-50" v-show="showHubspotPopup">
      <div class="fixed inset-0 bg-black/40" aria-hidden="true"></div>

      <div class="fixed inset-0 flex items-center justify-center px-5">
        <div class="relative my-auto min-w-[90vw] rounded-3xl bg-white px-2 md:min-w-[40vw] md:max-w-xl">
          <button @click="closePopup" class="absolute right-4 top-4">
            <svg width="26" height="25" viewBox="0 0 26 25" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12.5324 0C9.20978 0.00382858 6.02437 1.32528 3.67465 3.67485C1.32508 6.02421 0.00361099 9.20998 0 12.5326C0.688151 29.1581 24.3793 29.1535 25.0649 12.5326C25.0611 9.20978 23.7396 6.02457 21.3901 3.67485C19.0405 1.32528 15.8549 0.00381733 12.5323 0H12.5324ZM12.5324 23.8609V23.8611C9.52894 23.8577 6.64958 22.6631 4.52581 20.5392C2.40213 18.4156 1.20741 15.5361 1.20396 12.5327C1.82618 -2.49628 23.2413 -2.49215 23.8614 12.5327C23.8579 15.5361 22.6632 18.4155 20.5395 20.5392C18.4156 22.6631 15.5361 23.8576 12.5329 23.8611L12.5324 23.8609ZM17.6263 8.2898L13.3834 12.5327L17.6261 16.7754H17.6263C17.8612 17.0106 17.8612 17.3914 17.6263 17.6266C17.3913 17.8615 17.0103 17.8615 16.7753 17.6266L12.5324 13.3837L8.28969 17.6264V17.6266C8.05453 17.8615 7.67371 17.8615 7.43853 17.6266C7.20357 17.3914 7.20357 17.0106 7.43853 16.7754L11.6814 12.5327L7.43874 8.29H7.43853C7.20358 8.05484 7.20358 7.67402 7.43853 7.43884C7.67369 7.20388 8.05472 7.20388 8.2897 7.43884L12.5324 11.6818L16.7751 7.43905V7.43884C17.0115 7.21134 17.3863 7.21497 17.6182 7.4469C17.8502 7.67884 17.8538 8.05365 17.6263 8.29002L17.6263 8.2898Z"
                fill="#363636"
              ></path>
            </svg>
          </button>

          <TabGroup :selected-index="selectedPopupTabIndex" @change="changePopupTabIndex">
            <TabPanels class="min-h-[50vh]">
              <TabPanel class="min-h-full py-10">
                <div class="meetings-iframe-container" :data-src="meetingLink"></div>
              </TabPanel>
              <TabPanel class="min-h-full px-6 py-6">
                <NuxtImg class="mx-auto" src="/images/thank-you.svg" alt="thank-you" width="153" height="98" />
                <h1 class="mt-5 text-center text-2xl font-extrabold text-[#3a3a3c] md:text-5xl">Thank you</h1>
                <NuxtImg class="mx-auto mt-2" src="/images/underline.svg" alt="line" width="239" height="4" />
                <div v-if="!hubspotFormSubmit">
                  <p class="mx-auto mt-3 text-center text-base font-semibold text-[#3a3a3c] md:w-[29.625rem]">
                    You've just taken a rocking step in your customer success journey and we're so glad to be a part of
                    it.
                  </p>
                  <p class="mx-auto mb-6 mt-3 text-center text-base font-semibold text-[#3a3a3c] md:w-[29.625rem]">
                    We’d love to know more about you to assist you better!
                  </p>
                </div>
                <div v-else>
                  <p class="mx-auto mt-3 text-center text-base font-semibold text-[#3a3a3c] md:w-[29.625rem]">
                    Thank you for the details
                  </p>
                  <p class="mx-auto mb-6 mt-3 text-center text-base font-semibold text-[#3a3a3c] md:w-[29.625rem]">
                    Look forward to talking to you!
                  </p>
                </div>
                <div id="black-friday-form" class="custom-scrollbar max-h-[400px] overflow-y-scroll"></div>
              </TabPanel>
            </TabPanels>

            <TabList v-show="false">
              <Tab>1</Tab>
              <Tab>2</Tab>
            </TabList>
          </TabGroup>
        </div>
      </div>
    </div>

    <div id="video-dialog" class="relative z-50" v-if="showVideoPopup">
      <div class="fixed inset-0 bg-black/40" aria-hidden="true"></div>

      <div class="fixed inset-0 flex items-center justify-center">
        <div
          class="relative my-auto aspect-video min-w-[90vw] rounded-3xl bg-white px-4 pb-4 pt-10 md:min-w-[50vw] md:max-w-xl"
        >
          <button @click="closeVideoPopup" class="absolute right-3 top-3">
            <svg width="26" height="25" viewBox="0 0 26 25" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12.5324 0C9.20978 0.00382858 6.02437 1.32528 3.67465 3.67485C1.32508 6.02421 0.00361099 9.20998 0 12.5326C0.688151 29.1581 24.3793 29.1535 25.0649 12.5326C25.0611 9.20978 23.7396 6.02457 21.3901 3.67485C19.0405 1.32528 15.8549 0.00381733 12.5323 0H12.5324ZM12.5324 23.8609V23.8611C9.52894 23.8577 6.64958 22.6631 4.52581 20.5392C2.40213 18.4156 1.20741 15.5361 1.20396 12.5327C1.82618 -2.49628 23.2413 -2.49215 23.8614 12.5327C23.8579 15.5361 22.6632 18.4155 20.5395 20.5392C18.4156 22.6631 15.5361 23.8576 12.5329 23.8611L12.5324 23.8609ZM17.6263 8.2898L13.3834 12.5327L17.6261 16.7754H17.6263C17.8612 17.0106 17.8612 17.3914 17.6263 17.6266C17.3913 17.8615 17.0103 17.8615 16.7753 17.6266L12.5324 13.3837L8.28969 17.6264V17.6266C8.05453 17.8615 7.67371 17.8615 7.43853 17.6266C7.20357 17.3914 7.20357 17.0106 7.43853 16.7754L11.6814 12.5327L7.43874 8.29H7.43853C7.20358 8.05484 7.20358 7.67402 7.43853 7.43884C7.67369 7.20388 8.05472 7.20388 8.2897 7.43884L12.5324 11.6818L16.7751 7.43905V7.43884C17.0115 7.21134 17.3863 7.21497 17.6182 7.4469C17.8502 7.67884 17.8538 8.05365 17.6263 8.29002L17.6263 8.2898Z"
                fill="#363636"
              ></path>
            </svg>
          </button>

          <iframe
            :src="videoLink"
            title="video"
            allow="autoplay"
            class="mx-auto aspect-video w-full rounded-md"
          ></iframe>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
:deep(.meetings-iframe-container > iframe) {
  @apply max-h-[80vh];
  @apply pt-5;
}

.line-through-text {
  text-decoration: line-through #44b96d 2px;
}

button.faq-accordion.active {
  background-color: #fdf9f0;
}

div.faq-panel {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.2s ease-out;
}

.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
}
.custom-scrollbar {
  scrollbar-width: 8px;
  scrollbar-color: #d3d3d3 #f0efef;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: #f0efef;
  border-radius: 0 10px 10px 0;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: #d3d3d3;
  border-radius: 6px;
  border: 2px solid #f0efef;
}
</style>
